<template>
  <div
    id="view-ticket-details-modal"
    ref="modal"
    class="modal fade sp-modal"
    tabindex="-1"
    role="dialog"
    aria-labelledby="ticket-details-title"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg modal-dialog-scrollable" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <div class="sp-detail-head">
            <span v-if="ticket" class="sp-type-icon" :class="typeInfo.tone" aria-hidden="true">
              <i class="bi" :class="typeInfo.icon"></i>
            </span>
            <div>
              <div class="sp-kicker">Ticket #{{ ticketId }}</div>
              <h5 id="ticket-details-title" class="modal-title">
                {{ ticket ? ticket.type : loadError ? "Ticket unavailable" : "Loading…" }}
              </h5>
              <div v-if="ticket" class="sp-modal-meta">
                <span class="sp-pill" :style="pillStyle(meta)">
                  <i class="bi" :class="meta.icon"></i> {{ meta.label }}
                </span>
                <span>{{ unitLabel(ticket.unit) }}</span>
                <span aria-hidden="true">·</span>
                <span>Filed {{ formatDateTime(ticket.created_at) }}</span>
              </div>
            </div>
          </div>
          <button type="button" class="close" data-dismiss="modal" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="loading" class="sp-loading">
            <span class="spinner-border spinner-border-sm mr-1"></span> Loading ticket…
          </div>
          <div v-else-if="loadError" class="sp-error m-0" role="alert">
            <i class="bi bi-exclamation-triangle"></i> {{ loadError }}
          </div>

          <template v-else-if="ticket">
            <ol class="sp-progress" aria-label="Ticket progress">
              <li
                v-for="item in progress"
                :key="item.key"
                :class="item.state"
                :aria-current="item.state === 'current' ? 'step' : null"
              >
                <span class="sp-progress-dot">
                  <i v-if="item.state === 'done'" class="bi bi-check"></i>
                </span>
                <span class="sp-progress-label">{{ item.label }}</span>
              </li>
            </ol>

            <div class="sp-hint">
              <i class="bi" :class="meta.icon" :style="{ color: meta.textColor }"></i>
              <span>{{ statusHint }}</span>
            </div>

            <dl class="sp-facts">
              <div>
                <dt>Requested by</dt>
                <dd>{{ ticket.creator_name || "—" }}</dd>
              </div>
              <div>
                <dt>Unit/Section</dt>
                <dd>{{ unitLabel(ticket.unit) }}</dd>
              </div>
              <div>
                <dt>Assigned to</dt>
                <dd>
                  <template v-if="ticket.needs_assignment">
                    <span aria-label="Nobody yet">—</span>
                    <span class="sp-needs-tech inline">Needs a technician</span>
                  </template>
                  <template v-else>{{ ticket.assigned_name || "—" }}</template>
                  <span v-if="abilities.is_assignee" class="sp-you">you</span>
                  <button
                    v-if="abilities.assign && !mode"
                    type="button"
                    class="sp-link-btn sp-reassign"
                    @click="startMode('assign')"
                  >
                    <i class="bi bi-person-plus"></i> {{ ticket.needs_assignment ? "Assign" : "Reassign" }}
                  </button>
                </dd>
              </div>
              <div>
                <dt>{{ ticket.closed_at ? "Closed" : "Open for" }}</dt>
                <dd v-if="ticket.closed_at">
                  {{ formatDateTime(ticket.closed_at) }}
                  <span class="sp-sub">
                    (took {{ shortDuration(ticket.created_at, ticket.closed_at) }})
                  </span>
                </dd>
                <dd v-else>{{ shortAge(ticket.created_at) }}</dd>
              </div>
            </dl>

            <section class="sp-section">
              <h6 class="sp-section-title">Request</h6>
              <p class="sp-text">{{ ticket.description }}</p>
            </section>

            <section class="sp-section">
              <h6 class="sp-section-title">
                Findings
                <span v-if="!abilities.update_findings" class="sp-section-note">
                  recorded by the assigned technician
                </span>
              </h6>
              <template v-if="abilities.update_findings">
                <div class="form-group">
                  <label for="ticket-diagnosis" class="sp-label">Issues found</label>
                  <textarea
                    id="ticket-diagnosis"
                    v-model="draft.diagnosis"
                    class="form-control"
                    rows="3"
                    maxlength="5000"
                    placeholder="What was wrong?"
                  ></textarea>
                </div>
                <div class="form-group mb-0">
                  <label for="ticket-actions" class="sp-label">Actions taken</label>
                  <textarea
                    id="ticket-actions"
                    v-model="draft.actions_taken"
                    class="form-control"
                    rows="3"
                    maxlength="5000"
                    placeholder="What was done to fix it?"
                  ></textarea>
                </div>
              </template>
              <div v-else-if="ticket.diagnosis || ticket.actions_taken" class="sp-findings">
                <div v-if="ticket.diagnosis">
                  <div class="sp-label">Issues found</div>
                  <p class="sp-text">{{ ticket.diagnosis }}</p>
                </div>
                <div v-if="ticket.actions_taken">
                  <div class="sp-label">Actions taken</div>
                  <p class="sp-text">{{ ticket.actions_taken }}</p>
                </div>
              </div>
              <p v-else class="sp-muted">No findings recorded yet.</p>
            </section>

            <section v-if="referral" class="sp-section">
              <h6 class="sp-section-title">
                Job order referral
                <span v-if="referralCount > 1" class="sp-section-note">
                  latest of {{ referralCount }}
                </span>
              </h6>
              <div class="sp-box">
                <div class="d-flex flex-wrap align-items-center" style="gap: 8px">
                  <strong>Referral #{{ referral.id }}</strong>
                  <span class="sp-pill" :style="pillStyle(referralMeta)">
                    <i class="bi" :class="referralMeta.icon"></i> {{ referralMeta.label }}
                  </span>
                  <router-link
                    v-if="canManageReferrals"
                    class="ml-auto small"
                    :to="{ path: '/support/job-order-referrals', query: { referral: String(referral.id) } }"
                    @click.native="hide"
                  >
                    Open in Job Order Referrals
                  </router-link>
                </div>
                <div class="sp-sub mt-1">
                  Sent to General Services {{ formatDateTime(referral.created_at) }}
                  <template v-if="referral.closed_at">
                    · completed {{ formatDateTime(referral.closed_at) }}
                  </template>
                </div>
                <div v-if="referral.remarks" class="mt-2">
                  <div class="sp-label">General Services remarks</div>
                  <p class="sp-text">{{ referral.remarks }}</p>
                </div>
              </div>
            </section>

            <section
              v-if="ticket.status === 'Closed' && (ticket.feedback || abilities.feedback)"
              class="sp-section"
            >
              <h6 class="sp-section-title">Feedback</h6>
              <p v-if="ticket.feedback" class="sp-text">{{ ticket.feedback }}</p>
              <template v-else>
                <label for="ticket-feedback" class="sp-label">
                  How did it go? Your feedback helps the support team.
                </label>
                <textarea
                  id="ticket-feedback"
                  v-model="feedbackDraft"
                  class="form-control"
                  rows="2"
                  maxlength="2000"
                  placeholder="Optional — e.g. fixed quickly, or what could be better"
                ></textarea>
                <div class="text-right mt-2">
                  <button
                    type="button"
                    class="btn btn-sm btn-primary"
                    :disabled="!feedbackDraft.trim() || !!busy"
                    @click="sendFeedback"
                  >
                    <span
                      v-if="busy === 'feedback'"
                      class="spinner-border spinner-border-sm mr-1"
                    ></span>
                    Send feedback
                  </button>
                </div>
              </template>
            </section>

            <section v-if="timeline.length" class="sp-section">
              <h6 class="sp-section-title">History</h6>
              <ol class="sp-timeline">
                <li v-for="(event, index) in timeline" :key="index">
                  {{ event.label }}
                  <span class="sp-timeline-when">{{ formatDateTime(event.at) }}</span>
                </li>
              </ol>
            </section>

            <div v-if="mode === 'close'" ref="panel" class="sp-panel success">
              <p class="sp-panel-title">Close ticket #{{ ticket.id }}?</p>
              <p v-if="abilities.is_owner">
                Close it once the problem is fixed. You can reopen it later if the
                problem comes back.
              </p>
              <p v-else>
                {{ ticket.creator_name || "The requester" }} will be notified.
                <template v-if="findingsChanged">Your findings are saved with it.</template>
              </p>
              <p v-if="referral && referral.status === 'Ongoing'">
                Its open job order referral (#{{ referral.id }}) closes too, and
                General Services is told.
              </p>
              <label for="ticket-close-comment" class="sp-label">
                {{
                  abilities.is_owner
                    ? "Feedback (optional)"
                    : "Closing note (optional, added to Actions taken)"
                }}
              </label>
              <textarea
                id="ticket-close-comment"
                v-model="comment"
                class="form-control"
                rows="2"
                maxlength="2000"
              ></textarea>
              <div class="sp-panel-buttons">
                <button type="button" class="btn btn-light" :disabled="!!busy" @click="cancelMode">
                  Cancel
                </button>
                <button type="button" class="btn btn-success" :disabled="!!busy" @click="confirmClose">
                  <span v-if="busy === 'close'" class="spinner-border spinner-border-sm mr-1"></span>
                  Close ticket
                </button>
              </div>
            </div>

            <div v-if="mode === 'reopen'" ref="panel" class="sp-panel warn">
              <p class="sp-panel-title">Reopen ticket #{{ ticket.id }}?</p>
              <p>
                It goes back on your list{{
                  abilities.is_owner ? "" : ` and ${ticket.creator_name || "the requester"} is notified`
                }}.
              </p>
              <label for="ticket-reopen-reason" class="sp-label">
                What's still wrong? (optional)
              </label>
              <textarea
                id="ticket-reopen-reason"
                v-model="comment"
                class="form-control"
                rows="2"
                maxlength="1000"
              ></textarea>
              <div class="sp-panel-buttons">
                <button type="button" class="btn btn-light" :disabled="!!busy" @click="cancelMode">
                  Cancel
                </button>
                <button type="button" class="btn btn-warning" :disabled="!!busy" @click="confirmReopen">
                  <span v-if="busy === 'reopen'" class="spinner-border spinner-border-sm mr-1"></span>
                  Reopen ticket
                </button>
              </div>
            </div>

            <div v-if="mode === 'hold'" ref="panel" class="sp-panel hold">
              <p class="sp-panel-title">Put ticket #{{ ticket.id }} on hold?</p>
              <p>
                Use this while you wait on {{ ticket.creator_name || "the requester" }}, for example until
                the unit is brought to the MIS office. They're told why.
                <template v-if="findingsChanged">Your findings are saved with it.</template>
              </p>
              <label for="ticket-hold-reason" class="sp-label">Reason</label>
              <textarea
                id="ticket-hold-reason"
                v-model="comment"
                class="form-control"
                rows="2"
                maxlength="500"
              ></textarea>
              <div class="sp-panel-buttons">
                <button type="button" class="btn btn-light" :disabled="!!busy" @click="cancelMode">
                  Cancel
                </button>
                <button
                  type="button"
                  class="btn btn-secondary"
                  :disabled="!!busy || !comment.trim()"
                  @click="confirmHold"
                >
                  <span v-if="busy === 'hold'" class="spinner-border spinner-border-sm mr-1"></span>
                  Put on hold
                </button>
              </div>
            </div>

            <div v-if="mode === 'resume'" ref="panel" class="sp-panel info">
              <p class="sp-panel-title">Resume ticket #{{ ticket.id }}?</p>
              <p>
                It goes back to In progress and {{ ticket.creator_name || "the requester" }} is told.
              </p>
              <label for="ticket-resume-note" class="sp-label">Note (optional)</label>
              <textarea
                id="ticket-resume-note"
                v-model="comment"
                class="form-control"
                rows="2"
                maxlength="500"
              ></textarea>
              <div class="sp-panel-buttons">
                <button type="button" class="btn btn-light" :disabled="!!busy" @click="cancelMode">
                  Cancel
                </button>
                <button type="button" class="btn btn-primary" :disabled="!!busy" @click="confirmResume">
                  <span v-if="busy === 'resume'" class="spinner-border spinner-border-sm mr-1"></span>
                  Resume work
                </button>
              </div>
            </div>

            <div v-if="mode === 'assign'" ref="panel" class="sp-panel info">
              <p class="sp-panel-title">
                {{
                  ticket.needs_assignment
                    ? `Assign a technician to ticket #${ticket.id}`
                    : abilities.is_assignee
                    ? `Hand ticket #${ticket.id} to someone else`
                    : `Assign ticket #${ticket.id}`
                }}
              </p>
              <p>
                They're notified and can work it from their own account, and
                {{ ticket.creator_name || "the requester" }} is told who's handling it.
                <template v-if="abilities.is_assignee && !abilities.is_handler && !abilities.is_owner && !ticket.needs_assignment">
                  It leaves your list once it's theirs.
                </template>
              </p>
              <PersonPicker
                v-model="assignTo"
                :current-assignee-id="ticket.needs_assignment ? null : ticket.assigned_to"
              />
              <label for="ticket-assign-note" class="sp-label mt-3">Note for them (optional)</label>
              <textarea
                id="ticket-assign-note"
                v-model="comment"
                class="form-control"
                rows="2"
                maxlength="500"
                placeholder="e.g. what's been tried so far"
              ></textarea>
              <div class="sp-panel-buttons">
                <button type="button" class="btn btn-light" :disabled="!!busy" @click="cancelMode">
                  Cancel
                </button>
                <button type="button" class="btn btn-primary" :disabled="!!busy || !assignTo" @click="confirmAssign">
                  <span v-if="busy === 'assign'" class="spinner-border spinner-border-sm mr-1"></span>
                  {{ assignTo ? `Assign to ${assignTo.name.split(" ")[0]}` : "Assign" }}
                </button>
              </div>
            </div>

            <div v-if="mode === 'forward'" ref="panel" class="sp-panel info">
              <p class="sp-panel-title">Forward to General Services?</p>
              <p>
                This creates a job order referral for GSS and lets
                {{ ticket.creator_name || "the requester" }} know.
                <template v-if="findingsChanged">Your findings are saved first.</template>
              </p>
              <div class="sp-panel-buttons">
                <button type="button" class="btn btn-light" :disabled="!!busy" @click="cancelMode">
                  Cancel
                </button>
                <button type="button" class="btn btn-primary" :disabled="!!busy" @click="confirmForward">
                  <span v-if="busy === 'forward'" class="spinner-border spinner-border-sm mr-1"></span>
                  Forward to GSS
                </button>
              </div>
            </div>
          </template>
        </div>

        <div class="modal-footer">
          <button
            v-if="ticket && abilities.reopen && !mode"
            type="button"
            class="btn btn-outline-secondary text-dark mr-auto"
            @click="startMode('reopen')"
          >
            <i class="bi bi-arrow-counterclockwise mr-1"></i> Reopen
          </button>
          <button type="button" class="btn btn-light" data-dismiss="modal">Done</button>
          <template v-if="ticket && !mode">
            <button
              v-if="ticket.needs_assignment && abilities.assign"
              type="button"
              class="btn btn-primary"
              :disabled="!!busy"
              @click="startMode('assign')"
            >
              <i class="bi bi-person-plus mr-1"></i> Assign technician
            </button>
            <button
              v-if="abilities.update_findings"
              type="button"
              class="btn btn-outline-primary"
              :disabled="!findingsChanged || !!busy"
              @click="saveFindings"
            >
              <span v-if="busy === 'save'" class="spinner-border spinner-border-sm mr-1"></span>
              Save findings
            </button>
            <button
              v-if="abilities.resume"
              type="button"
              class="btn btn-primary"
              :disabled="!!busy"
              @click="startMode('resume')"
            >
              <i class="bi bi-play-circle mr-1"></i> Resume
            </button>
            <button
              v-if="abilities.hold"
              type="button"
              class="btn btn-outline-secondary text-dark"
              :disabled="!!busy"
              @click="startMode('hold')"
            >
              <i class="bi bi-pause-circle mr-1"></i> Put on hold
            </button>
            <button
              v-if="abilities.forward"
              type="button"
              class="btn btn-outline-primary"
              :disabled="!!busy"
              @click="startMode('forward')"
            >
              <i class="bi bi-box-arrow-up-right mr-1"></i> Forward to GSS
            </button>
            <button
              v-if="abilities.close"
              type="button"
              class="btn btn-success"
              :disabled="!!busy"
              @click="startMode('close')"
            >
              <i class="bi bi-check2-circle mr-1"></i> Close ticket
            </button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  DEFAULT_HOLD_REASON,
  DEFAULT_RESUME_NOTE,
  UNITS,
  errorMessage,
  formatDateTime,
  pillStyle,
  referralStatusMeta,
  serviceType,
  shortAge,
  shortDuration,
  ticketStatusMeta,
} from "../../supportOptions";
import PersonPicker from "./PersonPicker.vue";

export default {
  name: "ViewTicketDetailsModal",
  components: { PersonPicker },
  props: {
    canManageReferrals: { type: Boolean, default: false },
  },
  data() {
    return {
      ticketId: null,
      ticket: null,
      referral: null,
      referralCount: 0,
      hold: null,
      timeline: [],
      abilities: {},
      draft: { diagnosis: "", actions_taken: "" },
      feedbackDraft: "",
      comment: "",
      assignTo: null,
      mode: "",
      loading: false,
      loadError: "",
      busy: "",
      seq: 0,
      skipDirtyCheck: false,
    };
  },
  computed: {
    meta() {
      return ticketStatusMeta(this.ticket && this.ticket.status);
    },
    referralMeta() {
      return referralStatusMeta(this.referral && this.referral.status);
    },
    typeInfo() {
      return serviceType(this.ticket && this.ticket.type);
    },
    /** Filed → (On hold) → In progress → (With GSS) → Closed, marking how far the ticket has got. */
    progress() {
      if (!this.ticket) return [];
      const status = this.ticket.status;
      const steps = [
        { key: "filed", label: "Filed" },
        { key: "assigned", label: "Assigned" },
      ];
      if (status === "On hold") steps.push({ key: "hold", label: "On hold" });
      steps.push({ key: "progress", label: "In progress" });
      if (status === "Forwarded to GSS" || this.referral) {
        steps.push({ key: "gss", label: "With GSS" });
      }
      steps.push({ key: "closed", label: "Closed" });

      const reached = {
        // A pending ticket is either still waiting for a technician or assigned.
        Pending: this.ticket.needs_assignment ? "filed" : "assigned",
        "On hold": "hold",
        "In progress": "progress",
        "Forwarded to GSS": "gss",
        Closed: "closed",
      }[status];
      const at = steps.findIndex((step) => step.key === reached);
      return steps.map((step, index) => {
        let state = "todo";
        if (index < at || (index === at && reached === "closed")) state = "done";
        else if (index === at) state = "current";
        return { ...step, state };
      });
    },
    // Only the assigned technician works the ticket.
    works() {
      return !!this.abilities.is_assignee;
    },
    handlerName() {
      return (this.ticket && this.ticket.assigned_name) || "the support team";
    },
    statusHint() {
      if (!this.ticket) return "";
      const status = this.ticket.status;
      if (status === "Pending" && this.ticket.needs_assignment) {
        return this.abilities.assign
          ? "New ticket. First, assign a technician to handle it, or take it on yourself."
          : "Waiting for a technician to be assigned. You'll be notified when someone is.";
      }
      if (status === "Pending") {
        if (this.works) return "Assigned to you. Record what you find to mark it in progress.";
        return this.abilities.is_handler
          ? `Assigned to ${this.handlerName}. Waiting for them to start.`
          : `Assigned to ${this.handlerName}. You'll be notified when they start.`;
      }
      if (status === "In progress") {
        return this.works ? "You're working on this." : `${this.handlerName} is working on this.`;
      }
      if (status === "On hold") {
        const reason = (this.hold && this.hold.reason) || "Waiting on the requester.";
        const since = this.hold && this.hold.held_at ? ` (since ${formatDateTime(this.hold.held_at)})` : "";
        return this.works
          ? `On hold${since}: ${reason} Resume it once that's sorted.`
          : `On hold${since}: ${reason} ${this.handlerName} continues once that's done.`;
      }
      if (status === "Forwarded to GSS") {
        return "Referred to General Services as a job order. The ticket closes when they complete it.";
      }
      if (status === "Closed") {
        if (this.works) return "This ticket is closed. If the problem comes back, reopen it.";
        return this.abilities.is_owner
          ? `This ticket is closed. If the problem comes back, ask ${this.handlerName} to reopen it.`
          : "This ticket is closed.";
      }
      return this.meta.hint || "";
    },
    findingsChanged() {
      if (!this.ticket || !this.abilities.update_findings) return false;
      return (
        this.draft.diagnosis.trim() !== (this.ticket.diagnosis || "").trim() ||
        this.draft.actions_taken.trim() !== (this.ticket.actions_taken || "").trim()
      );
    },
  },
  mounted() {
    $(this.$refs.modal)
      .on("hide.bs.modal", (event) => {
        if (event.target !== this.$refs.modal) return;
        if (this.findingsChanged && !this.skipDirtyCheck) {
          if (!window.confirm("Discard the findings you haven't saved?")) {
            event.preventDefault();
            return;
          }
        }
        this.skipDirtyCheck = false;
      })
      .on("hidden.bs.modal", (event) => {
        if (event.target !== this.$refs.modal) return;
        this.mode = "";
        this.$emit("hidden");
      });
  },
  beforeDestroy() {
    this.skipDirtyCheck = true;
    $(this.$refs.modal).off("hide.bs.modal hidden.bs.modal").modal("hide");
  },
  methods: {
    pillStyle,
    shortAge,
    shortDuration,
    formatDateTime,
    unitLabel(value) {
      const unit = UNITS.find((u) => u.value === value);
      return unit ? unit.label : value || "—";
    },
    open(ticketId) {
      this.ticketId = ticketId;
      this.ticket = null;
      this.referral = null;
      this.hold = null;
      this.timeline = [];
      this.abilities = {};
      this.mode = "";
      this.comment = "";
      this.feedbackDraft = "";
      this.loadError = "";
      this.loading = true;
      $(this.$refs.modal).modal("show");
      this.load();
    },
    hide() {
      $(this.$refs.modal).modal("hide");
    },
    load() {
      const seq = ++this.seq;
      return axios
        .get(`api/tickets/${this.ticketId}`)
        .then((res) => {
          if (seq === this.seq) this.apply(res.data);
        })
        .catch((err) => {
          if (seq !== this.seq) return;
          this.loadError = errorMessage(err, "Couldn't load this ticket.");
        })
        .finally(() => {
          if (seq === this.seq) this.loading = false;
        });
    },
    apply(payload) {
      const ticket = payload.ticket || null;
      this.ticket = ticket;
      this.ticketId = ticket ? ticket.id : this.ticketId;
      this.referral = payload.referral || null;
      this.referralCount = payload.referral_count || 0;
      this.hold = payload.hold || null;
      this.timeline = payload.timeline || [];
      this.abilities = payload.abilities || {};
      this.draft = {
        diagnosis: (ticket && ticket.diagnosis) || "",
        actions_taken: (ticket && ticket.actions_taken) || "",
      };
    },
    startMode(mode) {
      this.mode = mode;
      this.assignTo = null;
      // Hold and resume start from the usual wording; edit it as needed.
      this.comment = { hold: DEFAULT_HOLD_REASON, resume: DEFAULT_RESUME_NOTE }[mode] || "";
      this.$nextTick(() => {
        const panel = this.$refs.panel;
        if (!panel) return;
        panel.scrollIntoView({ behavior: "smooth", block: "nearest" });
        const field = panel.querySelector("input[type=search], textarea, .btn-success, .btn-primary, .btn-warning");
        if (field) field.focus({ preventScroll: true });
      });
    },
    cancelMode() {
      this.mode = "";
      this.comment = "";
    },
    findingsPayload() {
      return {
        diagnosis: this.draft.diagnosis,
        actions_taken: this.draft.actions_taken,
      };
    },
    done(res, title) {
      toastr.success(res.data.message, title);
      if (res.data.data) this.apply(res.data.data);
      this.mode = "";
      this.comment = "";
      this.$emit("changed");
    },
    fail(err, title) {
      toastr.error(errorMessage(err), title);
    },
    saveFindings() {
      if (!this.findingsChanged || this.busy) return;
      this.busy = "save";
      axios
        .put(`api/tickets/${this.ticket.id}`, this.findingsPayload())
        .then((res) => this.done(res, "Saved"))
        .catch((err) => this.fail(err, "Not saved"))
        .finally(() => {
          this.busy = "";
        });
    },
    confirmClose() {
      if (this.busy) return;
      const payload = { comment: this.comment.trim() };
      if (this.abilities.update_findings) Object.assign(payload, this.findingsPayload());

      this.busy = "close";
      axios
        .post(`api/support/tickets/${this.ticket.id}/close`, payload)
        .then((res) => this.done(res, "Ticket closed"))
        .catch((err) => this.fail(err, "Not closed"))
        .finally(() => {
          this.busy = "";
        });
    },
    confirmHold() {
      const reason = this.comment.trim();
      if (!reason || this.busy) return;
      this.busy = "hold";
      axios
        .post(`api/support/tickets/${this.ticket.id}/hold`, { reason, ...this.findingsPayload() })
        .then((res) => this.done(res, "On hold"))
        .catch((err) => this.fail(err, "Not put on hold"))
        .finally(() => {
          this.busy = "";
        });
    },
    confirmResume() {
      if (this.busy) return;
      this.busy = "resume";
      axios
        .post(`api/support/tickets/${this.ticket.id}/resume`, { note: this.comment.trim() })
        .then((res) => this.done(res, "Resumed"))
        .catch((err) => this.fail(err, "Not resumed"))
        .finally(() => {
          this.busy = "";
        });
    },
    confirmReopen() {
      if (this.busy) return;
      this.busy = "reopen";
      axios
        .post(`api/support/tickets/${this.ticket.id}/reopen`, { reason: this.comment.trim() })
        .then((res) => this.done(res, "Ticket reopened"))
        .catch((err) => this.fail(err, "Not reopened"))
        .finally(() => {
          this.busy = "";
        });
    },
    async confirmAssign() {
      if (this.busy || !this.assignTo) return;
      this.busy = "assign";
      try {
        // Unsaved findings go with it, so the next person sees them.
        if (this.findingsChanged) await axios.put(`api/tickets/${this.ticket.id}`, this.findingsPayload());
        const res = await axios.post("api/support/tickets/assign", {
          ids: [this.ticket.id],
          user_id: this.assignTo.id,
          note: this.comment.trim(),
        });
        toastr.success(res.data.message, "Assigned");
        this.$emit("changed");
        const payload = res.data.data;
        if (payload && payload.abilities && payload.abilities.can_view === false) {
          // Handed on: it isn't yours to see any more.
          this.skipDirtyCheck = true;
          this.hide();
          return;
        }
        if (payload) this.apply(payload);
        this.cancelMode();
      } catch (err) {
        this.fail(err, "Not assigned");
      } finally {
        this.busy = "";
      }
    },
    async confirmForward() {
      if (this.busy) return;
      this.busy = "forward";
      try {
        if (this.findingsChanged) {
          const saved = await axios.put(`api/tickets/${this.ticket.id}`, this.findingsPayload());
          if (saved.data.data) this.apply(saved.data.data);
        }
        const res = await axios.post("api/support/tickets/to-job-order-referrals", {
          ids: [this.ticket.id],
        });
        toastr.success(res.data.message, "Forwarded");
        this.mode = "";
        this.$emit("changed");
        await this.load();
      } catch (err) {
        this.fail(err, "Not forwarded");
      } finally {
        this.busy = "";
      }
    },
    sendFeedback() {
      const feedback = this.feedbackDraft.trim();
      if (!feedback || this.busy) return;
      this.busy = "feedback";
      axios
        .post(`api/support/tickets/${this.ticket.id}/feedback`, { feedback })
        .then((res) => {
          this.feedbackDraft = "";
          this.done(res, "Feedback sent");
        })
        .catch((err) => this.fail(err, "Not sent"))
        .finally(() => {
          this.busy = "";
        });
    },
  },
};
</script>
